import { Component, DestroyRef, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Chart } from 'chart.js/auto';
import { ReportingService } from 'src/app/reporting/shared/services/reporting.service';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { getSolarErrorCodeString, getSolarWarnCodeString } from 'src/app/project/shared/enums/solar-error-messages.enum';
import { getSolarStatusTypeString } from 'src/app/project/shared/enums/solar-status.enum';
import { FUEL_CELL_CODE_NONE } from 'src/app/project/shared/enums/fuel-cell-status.enum';
import { EnergyGraphData } from '../../interfaces/energy-graph-data.interface';
import { FuelcellCurrentsDiagramComponent, FuelCellCurrentsDataPoint} from '../fuelcell-currents-diagram/fuelcell-currents-diagram.component';

export interface FuelCellDataPoint {
  generatorPower: number;
  generatorCurrent: number;
  generatorVoltage: number;
  loadStatusBattery: number;
  remainingFuel: number[];
  /** Number of the active cartridge, 0 without fuel manager */
  activePort: number;
  temperature: number;
  errorStatusActive: boolean;
  errorCode: string;
  fmErrorStatusActive: boolean;
  fmErrorCode: string;
  warnStatusActive: boolean;
  warnCode: string;
  time: string;
  totalRemainingFuel: number;
}

/** Keys of the individual charts, in display order (relevant for the export). */
type ChartKey = 'measurement' | 'efoyError' | 'voltage' | 'current' | 'power' | 'solarError';
const CHART_ORDER: ChartKey[] = ['measurement', 'efoyError', 'voltage', 'current', 'power', 'solarError'];

/** Kind of value each dataset of the solar error chart carries, in the order the datasets
 *  are created. Drives how the tooltip renders the value. */
const SOLAR_DATASET_KINDS = ['state', 'state', 'warnCode', 'warnCode', 'errorCode', 'errorCode'] as const;

@Component({
  selector: 'avs-energy-chart',
  templateUrl: './energy-chart.component.html',
  styleUrls: ['./energy-chart.component.css']
})
export class EnergyChartComponent implements OnInit, OnDestroy {

  public samId: number;
  public deviceName: string = "";
  public isFuelcell: boolean = false;
  /** Is there any data at all for the selected period? Controls whether the charts are
   *  shown and distinguishes "no data" from "data, but no errors". */
  public hasFuelCellData: boolean = false;
  public hasSolarData: boolean = false;
  public hasFuelCellWarnErrorStatus: boolean = false;
  public hasSolarWarnErrorStatus: boolean = false;
  
  public now = new Date();
  public nowAsISOString: string;
  public twoDaysAgoAsString: string;

  public fromDate: number = 2;
  public toDate: number = 0;
  public dataAge: number = 0;
  public fontSize: number = 10;
  public chartData: FuelCellDataPoint[] = [];
  private energyData: EnergyGraphData[] = [];
  // Measurement data arrays
  public timeLabels: string[] = [];  // Formatted time labels
  public generatorPower: number[] = [];
  public generatorVoltage: number[] = [];
  public generatorCurrent: number[] = [];
  public remainingFuel: number[] = [];
  public loadStatusBattery: number[] = [];
  public temperature: number[] = [];
  public activePort: number[] = [];

  // Error/warning arrays
  public errorWarningLevels: number[] = [];
  public errorDetails: string[] = [];

  // EFOY service URL
  public efoyBaseURL: string = `https://www.efoy-pro.com/service/servicetool/?product=efoy-pro-2800&errorcode=`;

  /** Active chart instances. Each one holds a reference to its canvas (chart.canvas),
   *  which is why neither destroyCharts() nor exportChart() needs global element IDs. */
  private readonly charts = new Map<ChartKey, Chart>();
  /** Canvases currently present in the DOM. */
  private readonly canvases = new Map<ChartKey, HTMLCanvasElement>();

  // Every canvas sits behind an *ngIf and does not exist yet when the data arrives.
  // The setters fire as soon as Angular inserts them – only then is the chart drawn.
  // When a canvas is hidden again, the setter fires with undefined.
  @ViewChild('measurementCanvas')
  private set measurementCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('measurement', ref); }
  @ViewChild('efoyErrorCanvas')
  private set efoyErrorCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('efoyError', ref); }
  @ViewChild('voltageCanvas')
  private set voltageCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('voltage', ref); }
  @ViewChild('currentCanvas')
  private set currentCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('current', ref); }
  @ViewChild('powerCanvas')
  private set powerCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('power', ref); }
  @ViewChild('solarErrorCanvas')
  private set solarErrorCanvasQuery(ref: ElementRef<HTMLCanvasElement> | undefined) { this.bindCanvas('solarError', ref); }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialog: MatDialog,
    private reportingService: ReportingService,
    private dialogRef: MatDialogRef<EnergyChartComponent>,
    private destroyRef: DestroyRef,
    private alertService: AlertService,
  ) {
    this.isFuelcell = data.schemaInfo?.fuelcell?.data != null;
    const lastData = new Date(this.data.schemaInfo?.solar?.data?.dataDateTime ?? NaN);
    const today = new Date();
    const diff = today.getTime() - lastData.getTime();
    const diffDays = diff / (1000 * 60 * 60 * 24);
    this.dataAge = diffDays;

    if (this.dataAge !== undefined && this.dataAge > 1) {
      this.toDate = Math.round(this.dataAge);
      this.fromDate = Math.round(this.dataAge) + 2;
    }

    this.samId = this.data.routeStation.samId;
    this.deviceName = this.data.routeStation.name;
    this.nowAsISOString = this.now.toISOString();

    const d = new Date();
    d.setDate(d.getDate() - 2);
    this.twoDaysAgoAsString = d.toISOString();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  ngOnInit(): void {
    this.getData();
  }

  private getData(): void {
    this.destroyCharts();
  
    this.resetDataArrays();

    if (this.isFuelcell) {
      this.reportingService.getFuelCellGraphics({ utcFrom: this.twoDaysAgoAsString, utcTo: this.nowAsISOString, samId: this.samId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            // Handle both response formats
            const data = Array.isArray(response) ? response : response?.data || [];
            if (!data || data.length === 0) {
              this.clearFuelCellData();
              return;
            }

            this.chartData = data;
            this.hasFuelCellData = true;
            this.processChartData();
            this.renderChart('measurement');
            this.renderChart('efoyError');
          },
          error: () => {
            this.clearFuelCellData();
            this.alertService.alert('Brennstoffzellendaten konnten nicht geladen werden', AlertTypes.error);
          }
        });
    }

    this.reportingService.getEnergyGraphics(
      { utcFrom: this.twoDaysAgoAsString, utcTo: this.nowAsISOString, samId: this.samId }
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: EnergyGraphData[]) => {
          if (!data || data.length === 0) {
            this.clearSolarData();
            return;
          }

          this.energyData = data;
          this.hasSolarData = true;
          this.hasSolarWarnErrorStatus =
            (data.find(d => d.errorWarnState1 > 0) != undefined) ||
            (data.find(d => (d.errorWarnState2 ?? 0) > 0) != undefined);

          this.renderChart('voltage');
          this.renderChart('current');
          this.renderChart('power');
          this.renderChart('solarError');
        },
        error: () => {
          this.clearSolarData();
          this.alertService.alert('Energiedaten konnten nicht geladen werden', AlertTypes.error);
        }
      });
  }

  /** Resets everything derived from the fuel cell data. The *ngIf then hides the
   *  corresponding canvases and the setters clean up the registry. */
  private clearFuelCellData(): void {
    this.chartData = [];
    this.resetDataArrays();
    this.hasFuelCellData = false;
    this.hasFuelCellWarnErrorStatus = false;
  }

  private clearSolarData(): void {
    this.energyData = [];
    this.hasSolarData = false;
    this.hasSolarWarnErrorStatus = false;
  }

  private destroyCharts(): void {
    this.charts.forEach(chart => chart.destroy());
    this.charts.clear();
  }

  /** Replaces an existing chart instance and registers the new one. */
  private setChart(key: ChartKey, chart: Chart): void {
    this.charts.get(key)?.destroy();
    this.charts.set(key, chart);
  }

  /** Links a canvas shown or hidden by *ngIf with its chart. */
  private bindCanvas(key: ChartKey, ref?: ElementRef<HTMLCanvasElement>): void {
    if (!ref) {
      this.canvases.delete(key);
      return;
    }
    this.canvases.set(key, ref.nativeElement);
    this.renderChart(key);
  }

  /** Draws a chart, but only if its canvas is already in the DOM. On the first call it
   *  is not; bindCanvas() then performs the drawing as soon as *ngIf has inserted the
   *  element. */
  private renderChart(key: ChartKey): void {
    const canvas = this.canvases.get(key);
    if (!canvas) { return; }

    switch (key) {
      case 'measurement': this.renderMeasurementChart(canvas); break;
      case 'efoyError':   this.renderEFOYErrorChart(canvas); break;
      case 'voltage':     this.renderVoltageChart(canvas); break;
      case 'current':     this.renderCurrentChart(canvas); break;
      case 'power':       this.renderPowerChart(canvas); break;
      case 'solarError':  this.renderSolarErrorChart(canvas); break;
    }
  }

  // 1) VOLTAGE: batteryVoltage & loadVoltage
  private renderVoltageChart(canvas: HTMLCanvasElement): void {
    const data = this.energyData;
    const labels = data.map(d => d.time);
    const batteryVolt = data.map(d => d.batteryVoltage);
    const loadVolt = data.map(d => d.loadVoltage);

    this.setChart('voltage', new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Batteriespannung [V]',
            data: batteryVolt,
            borderColor: 'blue',
            backgroundColor: 'rgba(0,0,255,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Lastspannung [V]',
            data: loadVolt,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.1)',
            fill: false,

            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

/*             borderDash: [3, 6],
 */          }
        ]
      },
      options: {
        aspectRatio: 5.0,
        scales: {
          x: {
            type: 'time',
            time: {
              // Parser removed – ISO is detected directly
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Spannung [V]' }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(labels[index]);
              }
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    }));
  }

  // 2) CURRENT: batteryCurrent1/2 & loadCurrent1/2
  private renderCurrentChart(canvas: HTMLCanvasElement): void {
    const data = this.energyData;
    const labels = data.map(d => d.time);
    const batteryCurrent1 = data.map(d => d.batteryCurrent1);
    const batteryCurrent2 = data.map(d => d.batteryCurrent2 ?? NaN);
    const loadCurrent1 = data.map(d => d.loadCurrent1);
    const loadCurrent2 = data.map(d => d.loadCurrent2 ?? NaN);

    this.setChart('current', new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Batteriestrom 1 [A]',
            data: batteryCurrent1,
            borderColor: 'green',
            backgroundColor: 'rgba(0,255,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Batteriestrom 2 [A]',
            data: batteryCurrent2,
            borderColor: 'darkgreen',
            backgroundColor: 'rgba(0,128,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: batteryCurrent2.every(v => Number.isNaN(v)),
          },
          {
            label: 'Laststrom 1 [A]',
            data: loadCurrent1,
            borderColor: 'purple',
            backgroundColor: 'rgba(128,0,128,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Laststrom 2 [A]',
            data: loadCurrent2,
            borderColor: 'magenta',
            backgroundColor: 'rgba(255,0,255,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: loadCurrent2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 5.0,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Strom [A]' }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(labels[index]);
              }
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    }));
  }

  // 3) POWER: solarPower1 & solarPower2?
  private renderPowerChart(canvas: HTMLCanvasElement): void {
    const data = this.energyData;
    const labels = data.map(d => d.time);
    const power1 = data.map(d => d.solarPower1);
    const power2 = data.map(d => d.solarPower2 ?? NaN);

    this.setChart('power', new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Solarleistung 1 [W]',
            data: power1,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Solarleistung 2 [W]',
            data: power2,
            borderColor: 'red',
            backgroundColor: 'rgba(255,0,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: power2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 5.0,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },

            },

            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Leistung [W]' }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(labels[index]);
              }
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    }));
  }

  /** Tooltip text for a value of the solar error chart – states and warning codes are
   *  shown as text, error codes stay numeric. */
  private formatSolarValue(datasetIndex: number, value: number): string {
    switch (SOLAR_DATASET_KINDS[datasetIndex]) {
      case 'state':
        return getSolarStatusTypeString(value);
      case 'warnCode':
        return getSolarWarnCodeString(value);
      case 'errorCode':
        return getSolarErrorCodeString(value);
      default:
        return String(value);
    }
  }

  // 4) ERROR/WARN: 6 lines
  private renderSolarErrorChart(canvas: HTMLCanvasElement): void {
    const data = this.energyData;
    const labels = data.map(d => d.time);
    const errorWarn1 = data.map(d => d.errorWarnState1);
    const errorWarn2 = data.map(d => d.errorWarnState2 ?? NaN);
    const warnCode1 = data.map(d => d.warnCode1);
    const warnCode2 = data.map(d => d.warnCode2 ?? NaN);
    const errorCode1 = data.map(d => d.errorCode1);
    const errorCode2 = data.map(d => d.errorCode2 ?? NaN);

    this.setChart('solarError', new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Status 1',
            data: errorWarn1,
            borderColor: 'red',
            backgroundColor: 'rgba(255,0,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

          },
          {
            label: 'Status 2',
            data: errorWarn2,
            borderColor: 'darkred',
            backgroundColor: 'rgba(139,0,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: errorWarn2.every(v => Number.isNaN(v)),
          },
          {
            label: 'WarnCode 1',
            data: warnCode1,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
          {
            label: 'WarnCode 2',
            data: warnCode2,
            borderColor: 'darkorange',
            backgroundColor: 'rgba(255,140,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: warnCode2.every(v => Number.isNaN(v)),
          },
          {
            label: 'Fehlercode Gerät 1',
            data: errorCode1,
            borderColor: 'purple',
            backgroundColor: 'rgba(128,0,128,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
          {
            label: 'Fehlercode Gerät 2',
            data: errorCode2,
            borderColor: 'magenta',
            backgroundColor: 'rgba(255,0,255,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: errorCode2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 5.0,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Codes / Status' }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(labels[index]);
              },
              label: (tooltipItem: any) => {
                const value = tooltipItem.parsed.y;
                return `${tooltipItem.dataset.label}: ${this.formatSolarValue(tooltipItem.datasetIndex, value)}`;
              }
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    }));
  }

  public removeData(): void {
    this.destroyCharts();
    this.getData();
  }
  private resetDataArrays(): void {
    this.timeLabels = [];
    this.generatorPower = [];
    this.generatorVoltage = [];
    this.generatorCurrent = [];
    this.loadStatusBattery = [];
    this.temperature = [];
    this.remainingFuel = [];
    this.activePort = [];
    this.errorWarningLevels = [];
    this.errorDetails = [];
  }

  private processChartData(): void {
    this.hasFuelCellWarnErrorStatus = false;
    this.chartData.forEach((dataPoint: FuelCellDataPoint, index: number) => {
      // Format time label (dd.MM HH:mm)
      const formattedTime = this.formatDateTime(dataPoint.time);
      this.timeLabels.push(dataPoint.time);

      // Measurement values
      this.generatorPower.push(dataPoint.generatorPower || 0);
      this.generatorVoltage.push(dataPoint.generatorVoltage || 0);
      this.generatorCurrent.push(dataPoint.generatorCurrent || 0);
      this.loadStatusBattery.push(dataPoint.loadStatusBattery <= 100 ? dataPoint.loadStatusBattery: -1);
      this.temperature.push(dataPoint.temperature || 0);
      this.remainingFuel.push(dataPoint.totalRemainingFuel || 0);
      this.activePort.push(dataPoint.activePort);

      // Determine error/warning level
      const errorLevel = this.determineErrorLevel(dataPoint);
      if (errorLevel > 0)
        this.hasFuelCellWarnErrorStatus = true;
      this.errorWarningLevels.push(errorLevel);

      // Store error details
      const errorDetail = this.buildErrorDetail(dataPoint);
      this.errorDetails.push(errorDetail);
    });
  }

  private formatDateTime(isoString: string): string {
    // Parse ISO string correctly
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  }

  private determineErrorLevel(dataPoint: FuelCellDataPoint): number {
    if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
      return 80;
    }
    if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
      return 60;
    }
    if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
      return 40;
    }
    return 0;
  }
  private buildErrorDetail(dataPoint: FuelCellDataPoint): string {
    const details: string[] = [];

    if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🔴 Fehlercode: ${dataPoint.errorCode}`);
    }
    if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🟠 FM Fehlercode: ${dataPoint.fmErrorCode}`);
    }
    if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🟡 Warncode: ${dataPoint.warnCode}`);
    }

    return details.join('\n');
  }

  /** Port 0 = single cartridge without fuel manager; a fuel manager may be added or removed within the series */
  private get hasPortZero(): boolean {
    return this.activePort.some(port => port === 0);
  }

  private get hasFuelManager(): boolean {
    return this.activePort.some(port => port > 0);
  }

  /** Highest cartridge number labelled on the axis: 2 with fuel manager, more only if higher ports actually occur */
  private get activePortAxisTop(): number {
    return this.hasFuelManager ? this.activePort.reduce((top, port) => Math.max(top, port), 2) : 0;
  }

  /** One step below port 0 if it occurs, so its line is not drawn on the lower edge */
  private get activePortAxisMin(): number {
    return this.hasPortZero ? -1 : 0;
  }

  /** One step above the highest cartridge, so its line is not drawn on the upper edge */
  private get activePortAxisMax(): number {
    return this.activePortAxisTop + 1;
  }

  /** Only the occurring port numbers are labelled (0 only if it occurs); the steps around them are only spacing */
  private isActivePortTickLabelled(value: number): boolean {
    return (value === 0 && this.hasPortZero) || (value >= 1 && value <= this.activePortAxisTop);
  }

  private renderMeasurementChart(canvas: HTMLCanvasElement): void {
    this.setChart('measurement', new Chart(canvas, {
      type: 'line',
      data: {
        labels: this.timeLabels,
        datasets: [
          {
            label: "Generatorleistung [W]",
            data: this.generatorPower,
            borderColor: 'rgba(0, 0, 255, 0.8)',
            backgroundColor: 'rgba(0, 0, 255, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: "Generatorspannung [V]",
            data: this.generatorVoltage,
            borderColor: 'rgba(255, 0, 0, 0.8)',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: "Generatorstrom [A]",
            data: this.generatorCurrent,
            borderColor: 'rgba(0, 255, 255, 0.8)',
            backgroundColor: 'rgba(0, 255, 255, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: "Ladezustand Batterie in %",
            data: this.loadStatusBattery,
            borderColor: 'rgba(255, 180, 0, 0.8)',
            backgroundColor: 'rgba(255, 180, 0, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: "Temperatur in °C",
            data: this.temperature,
            borderColor: 'rgba(0, 0, 0, 0.8)',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: "Verbleibender Gesamtkraftstoff in %",
            data: this.remainingFuel,
            borderColor: 'rgba(165, 42, 42, 0.8)',
            backgroundColor: 'rgba(165, 42, 42, 0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            // Cartridge number only changes in steps, so it is drawn stepped on its own axis
            label: "Aktive Patrone",
            data: this.activePort,
            yAxisID: 'yPort',
            stepped: true,
            borderColor: 'rgba(0, 128, 0, 0.8)',
            backgroundColor: 'rgba(0, 128, 0, 0.1)',
            fill: false,
            borderWidth: 2,
            // Solid line without a symbol per data point; the tooltip still works via the index mode
            pointRadius: 0,
            pointHoverRadius: 4,
          }
        ]
      },
      options: {
        // Flatter than the other charts would leave too little plot height: the seven legend entries
        // wrap to two rows and the right axis takes its share as well
        aspectRatio: 3.5,
        scales: {
          x: {
            type: 'time',
            time: {
              // Parser removed – ISO is detected directly
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: {
              display: true
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 20
            }
          },
          y: {
            title: {
              display: true,
              text: 'Messwerte'
            },
            min: -2,
            max: 150,
            position: 'left',
          },
          yPort: {
            title: {
              display: true,
              text: 'Aktive Patrone'
            },
            min: this.activePortAxisMin,
            max: this.activePortAxisMax,
            ticks: {
              stepSize: 1,
              callback: (value: string | number) => this.isActivePortTickLabelled(Number(value)) ? value : '',
            },
            grid: { drawOnChartArea: false },
            position: 'right',
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        onClick: (evt: any) => {
          this.handleMeasurementChartClick(evt);
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(this.timeLabels[index]);
              }
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      },
    }));
    console.log('✅ EFOY Measurement Chart erstellt');
  }

  private renderEFOYErrorChart(canvas: HTMLCanvasElement): void {
    this.setChart('efoyError', new Chart(canvas, {
      type: 'bar',
      data: {
        labels: this.timeLabels,
        datasets: [
          {
            label: "Fehler/Warnung",
            data: this.errorWarningLevels,
            backgroundColor: (context: any) => {
              const value = context.raw as number;
              if (value >= 80) return 'rgba(221, 26, 0, 0.8)';
              if (value >= 60) return 'rgba(255, 153, 0, 0.8)';
              if (value >= 40) return 'rgba(255, 255, 0, 0.8)';
              return 'rgba(0, 0, 0, 0)';
            },
            borderColor: (context: any) => {
              const value = context.raw as number;
              if (value >= 80) return 'rgba(140, 15, 0, 1)';
              if (value >= 60) return 'rgba(200, 120, 0, 1)';
              if (value >= 40) return 'rgba(200, 200, 0, 1)';
              return 'rgba(0, 0, 0, 0)';
            },
            borderWidth: 1,
            barPercentage: 1.0,
            categoryPercentage: 1.0
          }
        ]
      },
      options: {
        aspectRatio: 5.0,
        layout: {
          padding: {
            left: 13,
            right: 0,
            top: 0,
            bottom: 0
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              // Parser removed – ISO is detected directly
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: {
              display: true
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 20
            }
          },
          y: {
            title: {
              display: true,
              text: 'Fehler-Level'
            },
            min: 0,
            max: 100,
            ticks: {
              callback: (value: any) => {
                return("");
              }
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        onClick: (_evt: any, elements: any) => {
          this.handleErrorChartClick(elements);
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(this.timeLabels[index]);
              },
              afterBody: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                const errorDetail = this.errorDetails[index];
                return errorDetail ? ['', errorDetail] : [];
              }
            }
          },
          legend: {
            display: false
          }
        }
      },
    }));

    console.log('✅ EFOY Error Chart erstellt');
  }

  private handleMeasurementChartClick(evt: any): void {
    const chart = this.charts.get('measurement');
    if (!evt.native || !chart) return;

    const points = chart.getElementsAtEventForMode(
      evt.native,
      'nearest',
      { intersect: true },
      false
    );

    if (points.length > 0) {
      const index = points[0].index;
      if (this.chartData[index]) {
        this.reportingService
          .getFuelCellCurrents(this.samId, this.chartData[index].time)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((data) => {
              //console.log('(handleMeasurementChartClick) Stromfluss Diagramm data:', data);
              this.openStromflussDiagramm(data);
          });
      }
    }
  }

  /** Chart.js resolves the clicked elements with the chart's own interaction settings
   *  (mode 'index', intersect false). Using them keeps click and tooltip in sync: a click
   *  anywhere in a column hits the bar the tooltip is showing, not just the few pixels of
   *  the bar itself. */
  private handleErrorChartClick(elements: { index: number }[]): void {
    const index = elements[0]?.index;
    if (index === undefined) { return; }

    if (this.errorWarningLevels[index] > 0) {
      this.showErrorDetails(index);
    }
  }

  private showErrorDetails(index: number): void {
    const dataPoint = this.chartData[index];
    const errorDetail = this.errorDetails[index];

    if (!errorDetail) return;

    const message = `Zeit: ${this.timeLabels[index]}\n\n${errorDetail}`;
    const userConfirm = confirm(`${message}\n\nMöchten Sie die EFOY-Servicetool-Seite öffnen?`);

    if (userConfirm) {
      if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.errorCode, '_blank');
      } else if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.fmErrorCode, '_blank');
      } else if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.warnCode, '_blank');
      }
    }
  }

  private openStromflussDiagramm(cData: FuelCellCurrentsDataPoint): void {
    this.dialog.open(FuelcellCurrentsDiagramComponent, 
      {
        width: 'auto',
        data: cData
      }
    );
  }

  public setFromDate(date: number): void {
    this.fromDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.fromDate);
    this.twoDaysAgoAsString = d.toISOString();
  }

  public setToDate(date: number): void {
    this.toDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.toDate);
    this.nowAsISOString = d.toISOString();
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();
  }

  public exportChart(): void {
    var allCanvas: HTMLCanvasElement[] = [];
    var height: number = 0;
    var width: number = 0;
    // Every chart instance knows its own canvas – collect them in display order.
    CHART_ORDER.forEach(key => {
      const canvas = this.charts.get(key)?.canvas;
      if (canvas) {
        height += (canvas.height+20);
        width = canvas.width;
        allCanvas.push(canvas);
      }
    });

    if (allCanvas.length === 0) {
      this.alertService.alert('Es sind keine Diagramme zum Export vorhanden', AlertTypes.info);
      return;
    }

    const combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = width;
    combinedCanvas.height = height - 20;

    const ctx = combinedCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
      var x: number = 0;
      var currentY: number = 0;
      allCanvas.forEach(c=> {
        ctx.drawImage(c, x, currentY);
        currentY += (c.height+20);
      });

    let dataURL = combinedCanvas.toDataURL('image/png');
    let link = document.createElement('a');
    link.href = dataURL;
    link.download = `energie-verlauf-${this.samId}-${new Date().toISOString().split('T')[0]}.png`;
    link.click();
    }
  }
}
